/**
 * Ref Resolver — @-reference resolution for agent messages
 *
 * Extracts @/path references from user text and resolves them
 * against the workspace filesystem. Handles text files and
 * binary/image resolution for multimodal support.
 */
import type { Workspace } from '../types'
import type { AgentConfig, ResolvedRef } from '../agentTypes'

// ────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────

const REF_PATTERN = /@(\/[^\s,;)}\]]+)/g

/** Image MIME types that should be resolved as binary base64 */
const IMAGE_MIMES = new Set([
    'image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif',
])

/** Map file extensions to MIME types for image detection */
const EXT_TO_MIME: Record<string, string> = {
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.webp': 'image/webp',
    '.gif': 'image/gif',
}

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

/** Redact secrets from config text to prevent leaking to models */
export function redactConfigText(text: string, config: AgentConfig | null | undefined): string {
    if (!text) return text
    let next = text

    const apiKey = config?.provider?.apiKey
    if (apiKey && apiKey.length > 4) {
        next = next.split(apiKey).join('***REDACTED***')
    }

    const secrets = config?.secrets ?? []
    if (next.trim().startsWith('{') && secrets.length > 0) {
        try {
            const obj = JSON.parse(next) as Record<string, unknown>
            for (const path of secrets) {
                const parts = path.split('.')
                let target: Record<string, unknown> | null = obj
                for (let i = 0; i < parts.length - 1; i++) {
                    const child = target[parts[i]]
                    if (!child || typeof child !== 'object') { target = null; break }
                    target = child as Record<string, unknown>
                }
                if (target && Object.prototype.hasOwnProperty.call(target, parts[parts.length - 1])) {
                    target[parts[parts.length - 1]] = '***REDACTED***'
                }
            }
            next = JSON.stringify(obj, null, 2)
        } catch {
            // ignore parse failures and fall back to string replacement only
        }
    }

    return next
}

/** Parse a ref like /path/file.md:L10-L25 into path + optional line range */
export function parseRefPath(raw: string): { path: string; lineStart?: number; lineEnd?: number } {
    const match = raw.match(/^(.+?):L(\d+)(?:-L?(\d+))?$/)
    if (match) {
        return {
            path: match[1],
            lineStart: parseInt(match[2]),
            lineEnd: match[3] ? parseInt(match[3]) : parseInt(match[2]),
        }
    }
    return { path: raw }
}

/** Detect MIME type from file extension */
export function getMimeFromPath(path: string): string | null {
    const ext = path.substring(path.lastIndexOf('.')).toLowerCase()
    return EXT_TO_MIME[ext] ?? null
}

/** Check if a file path points to an image */
export function isImagePath(path: string): boolean {
    const mime = getMimeFromPath(path)
    return mime !== null && IMAGE_MIMES.has(mime)
}

// ────────────────────────────────────────────────────────────────
// Extraction & Resolution
// ────────────────────────────────────────────────────────────────

/** Extract all @-reference paths from user text */
export function extractRefs(text: string): string[] {
    const matches = [...text.matchAll(REF_PATTERN)]
    return matches.map(m => m[1])
}

/**
 * Resolve @-references in user text against the workspace.
 * - Text files are read as text content
 * - Image files are read as binary and base64-encoded
 */
export async function resolveRefs(
    text: string,
    workspace: Workspace,
    config?: AgentConfig | null,
    signal?: AbortSignal,
): Promise<ResolvedRef[]> {
    const rawPaths = extractRefs(text)
    const refs: ResolvedRef[] = []
    for (const raw of rawPaths) {
        const { path, lineStart, lineEnd } = parseRefPath(raw)
        const stat = workspace.fs.stat(path)

        // Check if this is an image file
        if (isImagePath(path)) {
            // Binary image resolution
            const bytes = await workspace.fs.readBinary(path, signal)
            if (bytes) {
                const mime = getMimeFromPath(path)!
                const base64 = bytesToBase64(bytes)
                refs.push({
                    path,
                    content: null,  // no text content for images
                    size: stat?.size,
                    isImage: true,
                    mime,
                    binaryBase64: `data:${mime};base64,${base64}`,
                })
            } else {
                refs.push({ path, content: null, size: stat?.size })
            }
            continue
        }

        // Text file resolution (existing logic)
        let content = await workspace.fs.readText(path, signal ? { signal } : undefined)
        // Slice to line range if specified
        if (content && lineStart !== undefined) {
            const lines = content.split('\n')
            const start = Math.max(0, lineStart - 1)
            const end = lineEnd !== undefined ? lineEnd : start + 1
            content = lines.slice(start, end).join('\n')
        }
        const safeContent = content ? redactConfigText(content, config) : content
        refs.push({ path, content: safeContent, size: stat?.size, lineStart, lineEnd })
    }
    return refs
}

// ────────────────────────────────────────────────────────────────
// Binary helpers
// ────────────────────────────────────────────────────────────────

/** Convert Uint8Array to base64 string */
function bytesToBase64(bytes: Uint8Array): string {
    // Use browser API for efficiency
    let binary = ''
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
}
